import { useContext, useEffect, useReducer, useState, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { isAdminProfile } from "../utils/isAdminProfile";
import ListOutlinedIcon from "@mui/icons-material/ListOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import { isArray } from "lodash";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import ForumIcon from "@mui/icons-material/Forum";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import Description from "@mui/icons-material/Description";
import DvrOutlinedIcon from "@mui/icons-material/DvrOutlined";
import Schedule from "@mui/icons-material/Schedule";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import PeopleIcon from "@mui/icons-material/People";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TimelineIcon from "@mui/icons-material/Timeline";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AttachFile from "@mui/icons-material/AttachFile";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import DeviceHubOutlined from "@mui/icons-material/DeviceHubOutlined";
import AllInclusive from "@mui/icons-material/AllInclusive";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CakeIcon from "@mui/icons-material/Cake";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import PhonelinkSetup from "@mui/icons-material/PhonelinkSetup";
import BusinessIcon from "@mui/icons-material/Business";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import WhatsAppIconMui from "@mui/icons-material/WhatsApp";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "./usePlans";
import { i18n } from "../translate/i18n";
import { empresaSoporta } from "../config/channelCapabilities";
import useCompanySettings from "./useSettings/companySettings";

const chatsReducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const newChats = [];
    if (isArray(action.payload)) {
      action.payload.forEach((chat) => {
        const idx = state.findIndex((u) => u.id === chat.id);
        if (idx !== -1) state[idx] = chat; else newChats.push(chat);
      });
    }
    return [...state, ...newChats];
  }
  if (action.type === "CHANGE_CHAT") {
    return state.map((chat) => chat.id === action.payload.chat.id ? action.payload.chat : chat);
  }
  return state;
};

// RDS-NAVGROUPS: fuente unica de verdad para la navegacion (sidebar desktop en
// MainListItems.js y el bottom-sheet "Mas" en layout/index.js). Cada item incluye
// `sub` (subtitulo descriptivo) y cada grupo incluye `color`/`bg` -- el sidebar
// desktop simplemente no renderiza esos campos, el menu movil si.
const useNavGroups = () => {
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket } = useContext(AuthContext);
  const location = useLocation();

  // "Nuevo" (reorg de menu con IA, 2026-10-23): punto morado en el
  // riel + badge NUEVO en el menu expandido para Tu dia, Agente IA y
  // Configuracion de IA -- se oculta por usuario despues de la primera
  // VISITA a esa pagina (no al primer render del menu). Persistido en
  // localStorage por usuario, mismo patron ya usado para "Enfoque
  // total" (layout/index.js, setNavFocusHiddenPersisted) -- decision
  // deliberada de NO sumar una tabla/endpoint nuevo solo para esto
  // (ver docs/pendientes-de-decision.md): la limitacion conocida es
  // que "visto" no viaja entre dispositivos de la misma persona.
  const NUEVO_ROUTE_TO_ID = { "/tu-dia": "tu-dia", "/shessai-ia": "agente-ia", "/configuracion-ia": "config-ia", "/agentes-ia": "agentes-ia" };
  const [seenMap, setSeenMap] = useState({});
  const seenStorageKeyRef = useRef(null);
  useEffect(() => {
    if (!user?.id) return;
    seenStorageKeyRef.current = `shessai-nuevo-visto:${user.id}`;
    try {
      const stored = JSON.parse(localStorage.getItem(seenStorageKeyRef.current) || "{}");
      setSeenMap(stored);
    } catch (e) { /* localStorage no disponible -- nunca bloquea el menu */ }
  }, [user?.id]);
  useEffect(() => {
    const id = NUEVO_ROUTE_TO_ID[location.pathname];
    if (!id || seenMap[id]) return;
    setSeenMap((prev) => {
      const next = { ...prev, [id]: true };
      try { if (seenStorageKeyRef.current) localStorage.setItem(seenStorageKeyRef.current, JSON.stringify(next)); } catch (e) { /* no bloquea */ }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showShessaiIA, setShowShessaiIA] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);
  const [showWallets, setShowWallets] = useState(false);
  const [hasUnseenDaily, setHasUnseenDaily] = useState(false);
  const [invisible, setInvisible] = useState(true);
  const [pageNumber] = useState(1);
  const [chats, dispatch] = useReducer(chatsReducer, []);

  const { get: getSetting } = useCompanySettings();
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    getSetting({ column: "DirectTicketsToWallets" })
      .then(s => setShowWallets(s.DirectTicketsToWallets))
      .catch(toastError);
  }, []);

  useEffect(() => {
    if (!user?.companyId) return;
    getPlanCompany(undefined, user.companyId)
      .then(p => {
        setShowCampaigns(p.plan.useCampaigns);
        setShowKanban(p.plan.useKanban);
        setShowOpenAi(p.plan.useOpenAi);
        setShowShessaiIA(p.plan.useShessaiIA);
        setShowIntegrations(p.plan.useIntegrations);
        setShowSchedules(p.plan.useSchedules);
        setShowInternalChat(p.plan.useInternalChat);
        setShowExternalApi(p.plan.useExternalApi);
      })
      .catch(toastError);
  }, [user?.companyId]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/chats/", { params: { searchParam: "", pageNumber } })
        .then(r => dispatch({ type: "LOAD_CHATS", payload: r.data.records }))
        .catch(toastError);
    }, 500);
    return () => clearTimeout(t);
  }, [pageNumber]);

  useEffect(() => {
    if (!user.id || !socket || typeof socket.on !== "function") return;
    const eventName = `company-${user.companyId}-chat`;
    const handler = (data) => {
      if (data.action === "new-message" || data.action === "update")
        dispatch({ type: "CHANGE_CHAT", payload: data });
    };
    socket.on(eventName, handler);
    return () => socket.off?.(eventName, handler);
  }, [socket, user.id, user.companyId]);

  useEffect(() => {
    let unreads = 0;
    chats.forEach(c => c.users?.forEach(cu => { if (cu.userId === user.id) unreads += cu.unreads; }));
    setInvisible(unreads === 0);
  }, [chats, user.id]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (whatsApps.length > 0) {
        setConnectionWarning(whatsApps.some(w =>
          ["qrcode", "PAIRING", "DISCONNECTED", "TIMEOUT", "OPENING"].includes(w.status)
        ));
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [whatsApps]);

  // Menu "Tu dia": punto de tarjetas nuevas sin ver. Liviano a
  // proposito (nunca dispara el calculo del dia) -- solo se consulta
  // si el plan incluye Shessai IA.
  useEffect(() => {
    if (!showShessaiIA) {
      setHasUnseenDaily(false);
      return;
    }
    api.get("/ai-agent/daily/unseen")
      .then(r => setHasUnseenDaily(Boolean(r.data?.hasUnseen)))
      .catch(() => {});
  }, [showShessaiIA]);

  const isAdmin = isAdminProfile(user.profile) || user.allowConnections === "enabled" || user.showDashboard === "enabled";
  const chatBadge = invisible ? 0 : chats.reduce((acc, c) => {
    c.users?.forEach(cu => { if (cu.userId === user.id) acc += cu.unreads; });
    return acc;
  }, 0);

  // Ver TAREA: capacidades por canal, aplicada al menú (Fase 4).
  // Superadmin ve todo -- mismo overrideAll que ya usa useChannelCapabilities,
  // calculado acá aparte porque este hook no lo consume (evita anidar un
  // hook dentro de otro solo para 3 valores booleanos).
  const overrideAllCapacidades = Boolean(user?.super);
  const puedeCampanas = empresaSoporta(whatsApps, "campanas", { overrideAll: overrideAllCapacidades });
  const puedeFlujos = empresaSoporta(whatsApps, "flujos", { overrideAll: overrideAllCapacidades });
  const puedeFrases = empresaSoporta(whatsApps, "frasesAutomaticas", { overrideAll: overrideAllCapacidades });

  const navGroups = useMemo(() => [
    {
      // REORG DE MENU CON IA (diseño aprobado, 2026-10-23): orden fijo
      // Inbox, Kanban, Contactos, Chat interno, Tu día, Marketing,
      // Llamadas IA -- "Tu día" ya NO vive en /shessai-ia, tiene su
      // propia pagina (/tu-dia). "Shessai IA" se renombra a "Agente IA"
      // y se muda a Automatización (es la IA que atiende al CLIENTE,
      // no una tarea de Atención). Cada item respeta la MISMA
      // condicion de plan/permiso que ya tenia donde vivia antes.
      id: "atencion", label: "Atención", color: "#0399FF", bg: "#eff6ff",
      items: [
        { to: "/tickets", icon: <WhatsAppIconMui />, label: "Inbox", sub: "Conversaciones activas" },
        ...(showKanban ? [{ to: "/kanban", icon: <ViewColumnIcon />, label: i18n.t("mainDrawer.listItems.kanban"), sub: "Vista de tablero" }] : []),
        ...(user.showContacts === "enabled" ? [{ to: "/contacts", icon: <ContactPhoneOutlinedIcon />, label: i18n.t("mainDrawer.listItems.contacts"), sub: "Clientes y prospectos" }] : []),
        ...(showInternalChat ? [{ to: "/chats", icon: <ForumIcon />, label: i18n.t("mainDrawer.listItems.chats"), badge: chatBadge, badgeColor: "red", sub: "Comunicación del equipo" }] : []),
        ...(showShessaiIA ? [{ to: "/tu-dia", icon: <WbSunnyOutlinedIcon />, label: "Tu día", dot: hasUnseenDaily, nuevo: !seenMap["tu-dia"], sub: "Resumen y pendientes de hoy" }] : []),
        // Ver TAREA: módulo Marketing -- distinto del ítem viejo
        // "/campaigns" ("Mensajes masivos", en Automatización) -- ese es
        // el sistema de texto libre, este es segmentos + plantillas de
        // Meta + wallet.
        { to: "/marketing", icon: <CampaignOutlinedIcon />, label: "Marketing", sub: "Campañas y wallet" },
        ...(isAdmin ? [{ to: "/llamadas-ia", icon: <PhoneInTalkIcon />, label: "Llamadas IA", sub: "Asistentes de voz con IA" }] : []),
      ],
    },
    {
      id: "analisis", label: "Análisis", color: "#16a34a", bg: "#f0fdf4",
      items: [
        ...(isAdmin ? [
          // Ver TAREA: pantalla Perfil + reorganización de la barra --
          // renombrado de "Dashboard" a "Analíticas" (mismo to:"/", mismo
          // contenido, solo cambia la etiqueta) para que coincida con el
          // tab de la barra inferior que reemplazó a "Más".
          { to: "/", icon: <DashboardOutlinedIcon />, label: "Analíticas", sub: "Métricas en tiempo real" },
          { to: "/whatsapp-usage", icon: <ReceiptLongOutlinedIcon />, label: "Consumo de WhatsApp", sub: "Mensajes y costo por categoría" },
          { to: "/reports", icon: <Description />, label: i18n.t("mainDrawer.listItems.reports"), sub: "Historial y exportación" },
          { to: "/moments", icon: <DvrOutlinedIcon />, label: i18n.t("mainDrawer.listItems.chatsTempoReal"), sub: "Momentos del servicio" },
        ] : []),
        ...(showSchedules ? [{ to: "/schedules", icon: <Schedule />, label: i18n.t("mainDrawer.listItems.schedules"), sub: "Horarios de atención" }] : []),
        { to: "/tags", icon: <LocalOfferIcon />, label: i18n.t("mainDrawer.listItems.tags"), sub: "Etiquetas de tickets" },
        { to: "/quick-messages", icon: <FlashOnIcon />, label: i18n.t("mainDrawer.listItems.quickMessages"), sub: "Plantillas de mensaje" },
      ],
    },
    {
      // REORG DE MENU CON IA (2026-10-23): "Agente IA" (antes "Shessai
      // IA") vivia aca, aparte de "Agentes IA" -- unificado 2026-10-26
      // (decision del usuario): el agente de WhatsApp ahora es un
      // agente mas DENTRO de "Agentes IA" (/agentes-ia/whatsapp), asi
      // que esa entrada de menu aparte se quita. /shessai-ia redirige
      // a la pantalla nueva (ver routes/index.js), no se borra la
      // ruta de golpe. Mismo gate de siempre (showShessaiIA/plan.useShessaiIA).
      id: "automatizacion", label: "Automatización", color: "#d97706", bg: "#fffbeb",
      items: [
        // Tarea nocturna 2026-10-26 (Constructor de Agentes): mismo
        // gate que antes tenia "Agente IA" (showShessaiIA) -- la
        // pagina misma revisa Company.agentsEnabled y muestra un
        // estado vacio si esta apagada, nunca un menu roto para quien
        // no la tiene.
        ...(showShessaiIA ? [{ to: "/agentes-ia", icon: <AutoAwesomeIcon />, label: "Agentes IA", nuevo: !seenMap["agentes-ia"], sub: "Automatizaciones con permisos", accent: "#4c9bf5" }] : []),
        // Ver TAREA: capacidades por canal, aplicada al menú (Fase 4).
        // "campanas" y "frasesAutomaticas" fallan silenciosamente en
        // whatsapp_cloud_api (y campanas también en whatsapp_oficial) --
        // ver el inventario. puedeCampanas/puedeFlujos/puedeFrases se
        // calculan una vez arriba (junto a isAdmin/chatBadge) para no
        // repetir empresaSoporta() en cada item.
        ...(user.showFlow === "enabled" && puedeFlujos ? [
          { to: "/flowbuilders", icon: <TimelineIcon />, label: "Flujo de conversación", sub: "FlowBuilder visual" },
        ] : []),
        ...(showCampaigns && user.showCampaign === "enabled" && puedeCampanas ? [
          { to: "/campaigns", icon: <RecordVoiceOverIcon />, label: i18n.t("mainDrawer.listItems.campaigns"), sub: "Mensajes masivos" },
          { to: "/contact-lists", icon: <PeopleIcon />, label: i18n.t("campaigns.subMenus.listContacts"), sub: "Segmentación" },
          { to: "/campaigns-config", icon: <SettingsOutlinedIcon />, label: i18n.t("campaigns.subMenus.settings"), sub: "Ajustes de campañas" },
        ] : []),
        ...(user.showFlow === "enabled" && puedeFrases ? [
          { to: "/phrase-lists", icon: <EventAvailableIcon />, label: "Frases automáticas", sub: "Respuestas automatizadas" },
        ] : []),
        ...(isAdmin ? [{ to: "/files", icon: <AttachFile />, label: i18n.t("mainDrawer.listItems.files"), sub: "Documentos compartidos" }] : []),
      ],
    },
    {
      id: "admin", label: "Administración", color: "#7c3aed", bg: "#f5f3ff",
      items: [
        ...(isAdmin ? [
          { to: "/connections", icon: <SyncAltIcon />, label: i18n.t("mainDrawer.listItems.connections"), badge: connectionWarning ? 1 : 0, badgeColor: "amber", sub: "WhatsApp, Facebook, Instagram" },
          { to: "/users", icon: <PeopleAltOutlinedIcon />, label: i18n.t("mainDrawer.listItems.users"), sub: "Agentes y permisos" },
          { to: "/queues", icon: <AccountTreeOutlinedIcon />, label: i18n.t("mainDrawer.listItems.queues"), sub: "Equipos de trabajo" },
          ...(isAdminProfile(user.profile) ? [{ to: "/custom-fields", icon: <ListOutlinedIcon />, label: "Campos personalizados", sub: "Datos de contacto a medida" }] : []),
          ...(isAdminProfile(user.profile) ? [{ to: "/flujos", icon: <ListOutlinedIcon />, label: "Flujos", sub: "Bases de datos a medida" }] : []),
          ...(showIntegrations ? [{ to: "/queue-integration", icon: <DeviceHubOutlined />, label: i18n.t("mainDrawer.listItems.queueIntegration"), sub: "APIs y bots" }] : []),
          ...(showOpenAi ? [{ to: "/prompts", icon: <AllInclusive />, label: i18n.t("mainDrawer.listItems.prompts"), sub: "Asistente IA" }] : []),
          ...(isAdminProfile(user.profile) && showWallets ? [{ to: "/wallets", icon: <AccountBalanceWalletIcon />, label: i18n.t("mainDrawer.listItems.wallets"), sub: "Carteras de contactos" }] : []),
          { to: "/birthday-settings", icon: <CakeIcon />, label: i18n.t("mainDrawer.listItems.birthdaySettings"), sub: "Mensajes automáticos" },
          // REORG DE MENU CON IA (2026-10-23): "Facturación y consumo"
          // se mueve aqui desde Sistema (pedido explicito) -- agrega la
          // pestaña "Uso y cobro de IA" (ver pages/Financeiro), mismo
          // gate de siempre (isAdmin).
          { to: "/financeiro", icon: <LocalAtmIcon />, label: i18n.t("mainDrawer.listItems.financeiro"), sub: "Facturas, pagos y uso de IA" },
        ] : []),
        ...(user.super ? [
          { to: "/announcements", icon: <AnnouncementIcon />, label: i18n.t("mainDrawer.listItems.annoucements"), sub: "Boletines internos" },
          { to: "/allConnections", icon: <PhonelinkSetup />, label: i18n.t("mainDrawer.listItems.allConnections"), sub: "Vista global" },
          { to: "/companies", icon: <BusinessIcon />, label: i18n.t("mainDrawer.listItems.companies"), sub: "Multi-empresa" },
          { to: "/whatsapp-templates", icon: <DescriptionOutlinedIcon />, label: "Plantillas WhatsApp", sub: "Meta Cloud API" },
        ] : []),
      ],
    },
    {
      id: "sistema", label: "Sistema", color: "#6b7280", bg: "#f8f7f5",
      items: [
        ...(isAdmin ? [
          { to: "/settings", icon: <SettingsOutlinedIcon />, label: i18n.t("mainDrawer.listItems.settings"), sub: "Opciones del sistema" },
          // REORG DE MENU CON IA (2026-10-23): "Configuración de IA" --
          // antes la pestaña "Configuración" dentro de Shessai IA
          // (H1-H7), ahora su propia página bajo Sistema. Mismo gate
          // de siempre (showShessaiIA/plan.useShessaiIA).
          ...(showShessaiIA ? [{ to: "/configuracion-ia", icon: <AutoAwesomeIcon />, label: "Configuración de IA", nuevo: !seenMap["config-ia"], sub: "Contexto, voz, conocimiento, modelos y reglas" }] : []),
        ] : []),
        { to: "/notifications", icon: <NotificationsActiveOutlinedIcon />, label: "Notificaciones", sub: "Push y alertas" },
        ...(showExternalApi && isAdmin ? [{ to: "/messages-api", icon: <CodeRoundedIcon />, label: i18n.t("mainDrawer.listItems.messagesAPI"), sub: "Documentación" }] : []),
        // Ver TAREA: centro de ayuda del CRM (Fase 4, punto 7). Antes este
        // ítem apuntaba a /helps (la galería de videos) y solo existía si
        // hasHelps era true -- si tu empresa no había subido ningún video
        // todavía, no había forma de llegar a NINGÚN centro de ayuda desde
        // el menú. Ahora apunta siempre a /ayuda (el centro de contenido);
        // /helps sigue existiendo, pero se enlaza desde DENTRO del índice
        // de /ayuda (fila "Videos internos de tu empresa"), visible solo
        // cuando hasHelps es true -- ese check se movió a pages/Ayuda, ya
        // no vive acá.
        { to: "/ayuda", icon: <HelpOutlineIcon />, label: "Ayuda", sub: "Guías del CRM" },
      ],
    },
  ], [showCampaigns, showKanban, showOpenAi, showShessaiIA, showIntegrations, showSchedules,
      showInternalChat, showExternalApi, showWallets, hasUnseenDaily, isAdmin,
      connectionWarning, chatBadge, user, puedeCampanas, puedeFlujos, puedeFrases, seenMap]);

  // REORG DE MENU CON IA (diseño aprobado, 2026-10-23): el acceso
  // rápido superior (Tu día/Shessai IA) se QUITA -- Tu día y Agente IA
  // ahora viven solo en su lugar natural dentro de navGroups (Atención
  // y Automatización respectivamente), sin duplicados en ningún modo
  // (expandido, riel/asomar, móvil).
  const quickAccess = [];

  return { navGroups, quickAccess, isAdmin, chatBadge, connectionWarning, showShessaiIA };
};

export default useNavGroups;
